import { Link } from "react-router-dom";
import { ScrollText, Landmark, HeartHandshake, ShieldCheck, Check, ArrowUpRight, Scale } from "lucide-react";
import { PageHeader } from "@/components/site/PageHeader";
import { Reveal } from "@/components/site/Reveal";
import { CONTACT } from "@/data";

const PILLARS = [
  {
    icon: ScrollText,
    title: "The Will",
    sub: "Your wishes, made law.",
    body: "A last will and testament tells the world exactly what happens to everything you own — who receives it, who carries it out, and who cares for your minor children. Without one, the state decides for you.",
    points: ["Names guardians for your children", "Directs who inherits what", "Appoints your executor", "Prevents family disputes"],
  },
  {
    icon: Landmark,
    title: "The Trust",
    sub: "Control beyond a lifetime.",
    body: "A living trust holds your assets and passes them privately, quickly, and often outside of probate court — with rules you write. Protect an inheritance from being spent too fast, too young, or by the wrong hands.",
    points: ["Avoids the delays & cost of probate", "Keeps your estate private", "Controls when & how heirs receive assets", "Protects beneficiaries with special needs"],
  },
];

const HAND_IN_HAND = [
  { icon: HeartHandshake, title: "Life insurance funds the plan", body: "A will says who gets what — life insurance makes sure there's something to give. The tax-free death benefit creates instant liquidity the moment your family needs it most." },
  { icon: ScrollText, title: "The will gives it direction", body: "Your policy pays; your will and beneficiary designations direct. Aligning both keeps your money from landing in probate — or in the wrong hands." },
  { icon: Landmark, title: "The trust gives it protection", body: "Naming a trust as beneficiary lets you control the money after you're gone — paying out for education, milestones, or monthly income instead of one unprotected lump sum." },
  { icon: ShieldCheck, title: "Together, a complete shield", body: "Insurance without an estate plan is money without a map. An estate plan without insurance is a map without fuel. Heritage helps you build both — hand in hand." },
];

export default function WillTrustPage() {
  return (
    <div data-testid="willtrust-page">
      <PageHeader
        overline="Estate Planning · Wills & Trusts"
        title="Will & trust"
        accent="planning."
        subtitle="Your legacy deserves more than good intentions. We help families put their wishes in writing — and pair them with life insurance so the plan is funded from day one."
      />

      {/* Will vs Trust */}
      <section className="relative pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.06}>
              <div className="h-full rounded-3xl border border-[#bf953f]/25 bg-[#0a192f] p-8 md:p-10 hover:border-[#d4af37]/50 transition-colors duration-500" data-testid={`willtrust-pillar-${i}`}>
                <span className="inline-flex h-13 w-13 items-center justify-center rounded-xl border border-[#d4af37]/35 p-3 text-[#d4af37]">
                  <p.icon size={26} strokeWidth={1.4} />
                </span>
                <h2 className="mt-6 font-serif-display text-3xl md:text-4xl text-white">{p.title}</h2>
                <p className="mt-1 font-body text-sm italic text-[#d4af37]">{p.sub}</p>
                <p className="mt-4 font-body font-light text-base leading-relaxed text-white/65">{p.body}</p>
                <ul className="mt-6 space-y-3">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-3 font-body text-sm text-white/70">
                      <Check size={16} className="mt-0.5 shrink-0 text-[#d4af37]" /> {pt}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Hand in hand with life insurance */}
      <section className="relative py-16 md:py-24 border-t border-white/10" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(212,175,55,0.10), rgba(8,18,38,0) 60%)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-body text-xs md:text-sm tracking-[0.3em] uppercase text-[#d4af37]">Hand In Hand</p>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-white max-w-3xl">
              Life insurance and your will &amp; trust <span className="text-gold-gradient italic">were made for each other.</span>
            </h2>
            <p className="mt-5 max-w-3xl font-body font-light text-base md:text-lg leading-relaxed text-white/60">
              A will and trust decide <em>where</em> everything goes. Life insurance decides <em>whether there's enough to go around.</em> When
              the two are designed together — beneficiaries aligned, the trust funded, the coverage sized to your real
              obligations — your family skips the courtroom, skips the waiting, and steps straight into the future you
              planned for them.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {HAND_IN_HAND.map((h, i) => (
              <Reveal key={h.title} delay={i * 0.05}>
                <div className="h-full rounded-2xl border border-white/10 bg-[#0f2744]/60 p-7 hover:border-[#d4af37]/50 transition-colors duration-500" data-testid={`willtrust-hand-${i}`}>
                  <h.icon size={24} strokeWidth={1.4} className="text-[#d4af37]" />
                  <h3 className="mt-5 font-serif-display text-xl text-white leading-snug">{h.title}</h3>
                  <p className="mt-3 font-body font-light text-sm leading-relaxed text-white/60">{h.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 md:py-24 border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="rounded-3xl border border-[#d4af37]/30 bg-gradient-to-br from-[#12284a] to-[#0a192f] p-10 md:p-14 text-center">
              <Scale size={30} className="mx-auto text-[#d4af37]" strokeWidth={1.4} />
              <h2 className="mt-5 font-serif-display text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-white">
                Put your whole plan <span className="text-gold-gradient italic">in writing.</span>
              </h2>
              <p className="mt-4 mx-auto max-w-xl font-body font-light text-base md:text-lg text-white/60">
                One conversation covers it all — your will, your trust, and the life insurance that funds them. No cost,
                no pressure, just a plan.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <Link
                  to="/book-a-call"
                  data-testid="willtrust-book-btn"
                  className="btn-sweep inline-flex items-center gap-2 rounded-full bg-gold-metallic px-8 py-4 font-body text-sm font-semibold tracking-wide text-black"
                >
                  Book a Free Planning Call <ArrowUpRight size={15} />
                </Link>
                <a
                  href={CONTACT.phoneHref}
                  data-testid="willtrust-call-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-[#d4af37]/40 px-8 py-4 font-body text-sm tracking-wide text-white/80 hover:text-[#fcf6ba] hover:border-[#d4af37] transition-colors duration-300"
                >
                  Call {CONTACT.phone}
                </a>
              </div>
              <p className="mt-6 font-body text-[11px] text-white/35 max-w-lg mx-auto leading-relaxed">
                Heritage Family Protection Agency is not a law firm. Will and trust documents are prepared through
                partner legal service providers, including LegalShield provider law firms.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
